import axios from "axios";
import { DataTable } from "simple-datatables";
import { handleRequestError, showAlert } from "../utils/alert";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

export default () => ({
  hasAccessModule: true,
  reportList: [],
  hasSearched: false,

  summary: {
    total_revenue: 0,
    total_items_sold: 0,
    top_service: null,
    top_product: null,
  },

  dataTableInstance: null,

  filters: {
    start_date: "",
    end_date: "",
    item_type: "", // '' = Todos, 'service' = Serviços, 'product' = Produtos
  },

  fpStartDate: null,
  fpEndDate: null,

  async initPerformance() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // "Ping" na API para testar a permissão de acesso do usuário.
      try {
        await axios.get("/api/client/reports/performance");
      } catch (pingError) {
        // Se a API barrar por falta de permissão/plano, lança o erro para o catch principal
        if (pingError.response && pingError.response.status === 403) {
          throw pingError;
        }
        // Ignora erros como 422 (campos de data obrigatórios ausentes),
        // pois isso indica que o middleware de autorização autorizou a passagem.
      }

      // Usuário tem permissão, pode inicializar a interface
      this.initFlatpickr();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view imediatamente
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  initFlatpickr() {
    const config = {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      allowInput: true,
      altInput: true,
      altFormat: "d/m/Y",
    };
    this.fpStartDate = flatpickr(this.$refs.startDateInput, {
      ...config,
      onChange: (_, dateStr) => (this.filters.start_date = dateStr),
    });
    this.fpEndDate = flatpickr(this.$refs.endDateInput, {
      ...config,
      onChange: (_, dateStr) => (this.filters.end_date = dateStr),
    });
  },

  formatMoney(val) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  },

  getAbcBadge(abcClass) {
    if (abcClass === "A")
      return '<span class="bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-md font-bold text-xs">Curva A</span>';
    if (abcClass === "B")
      return '<span class="bg-blue-100 text-blue-800 border border-blue-200 px-2 py-0.5 rounded-md font-bold text-xs">Curva B</span>';
    return '<span class="bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-md font-bold text-xs">Curva C</span>';
  },

  async loadReportTable() {
    if (!this.filters.start_date || !this.filters.end_date) {
      return showAlert(
        "Por favor, selecione as datas Inicial e Final.",
        "warning",
      );
    }

    try {
      const queryParams = new URLSearchParams(this.filters).toString();
      const response = await axios.get(
        `/api/client/reports/performance?${queryParams}`,
      );

      this.reportList = response.data.data;
      this.summary = response.data.summary;
      this.hasSearched = true;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#performanceReportTable tbody");
      if (!tbody) return;

      tbody.innerHTML = this.reportList
        .map((row) => {
          const typeLabel = row.type === "service" ? "Serviço" : "Produto";
          return `
          <tr class="hover:bg-slate-50 border-b border-gray-100">
            <td class="p-3 text-sm font-bold text-slate-500 text-center">${row.position}º</td>
            <td class="p-3 text-sm font-bold text-slate-800">${row.name}</td>
            <td class="p-3 text-sm text-slate-500">${typeLabel}</td>
            <td class="p-3 text-sm font-mono text-center text-slate-700">${row.quantity}</td>
            <td class="p-3 text-sm font-mono font-bold text-emerald-600">${this.formatMoney(row.total_revenue)}</td>
            <td class="p-3 text-sm font-mono text-slate-500">${row.revenue_percent.toFixed(1)}%</td>
            <td class="p-3 text-center">${this.getAbcBadge(row.abc_class)}</td>
          </tr>
        `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#performanceReportTable", {
        searchable: false,
        sortable: false, // Desativado para não quebrar a ordem ABC do Backend
        perPageSelect: false,
        labels: {
          placeholder: "Pesquisar item...",
          searchTitle: "Pesquisar",
          noRows: "Nenhum dado de venda encontrado.",
          info: "Mostrando {start} a {end} de {rows} itens",
        },
      });
    } catch (error) {
      // Bloqueia caso o plano vença ou mude durante o uso da tela
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }
      handleRequestError(error, "Erro ao processar relatório de desempenho.");
    }
  },

  applyFilters() {
    this.loadReportTable();
  },

  exportToCSV() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados para exportar.", "warning");
    let csvContent =
      "\uFEFFPosição;Item;Tipo;Qtd Vendida;Faturamento;%;Curva ABC\n";

    this.reportList.forEach((row) => {
      const tipo = row.type === "service" ? "Serviço" : "Produto";
      csvContent += `"${row.position}º";"${row.name}";"${tipo}";"${row.quantity}";"${row.total_revenue.toFixed(2)}";"${row.revenue_percent.toFixed(2)}%";"${row.abc_class}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `curva_abc_${this.filters.start_date}_a_${this.filters.end_date}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  async exportToPDF() {
    if (this.reportList.length === 0)
      return showAlert("Não há dados.", "warning");

    try {
      const response = await axios.post(
        "/api/client/reports/request-download-token",
      );
      const downloadToken = response.data.download_token;

      const queryParams = new URLSearchParams(this.filters);
      queryParams.append("dtoken", downloadToken);

      window.open(
        `/client/reports/performance/pdf?${queryParams.toString()}`,
        "_blank",
      );
    } catch (error) {
      handleRequestError(
        error,
        "Erro ao solicitar autorização para impressão.",
      );
    }
  },
});
